<template>
    <b-card no-body>
      <b-card-body class="filter-container">
        <filter-panel-new
          class="filter-panel"
          :apply-filter-immediately="false"
          :filter-groups="filterGroups"
          @update="applyFilter"
          :notification-text="null"
          :api-request-data="apiRequestData">
          <template #panel-actions>
            <div class="request-controls">
              <b-form-checkbox
                class="checkbox"
                @input="() => {}"
                v-model="autoRefresh"
                :disabled="apiRequestIsPerforming">
                Auto-refresh
              </b-form-checkbox>
              <b-button
                :disabled="apiRequestIsPerforming"
                @click="forceRefreshFilter"
                class="button"
                size="sm"
                :variant="refreshButtonVariant">
                Refresh
              </b-button>
            </div>
          </template>
        </filter-panel-new>
      </b-card-body>
    </b-card>
</template>

<script>
import getNested from 'lodash-es/get';
import cloneDeep from 'lodash-es/cloneDeep';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import FilterPanelNew, {
  getCurrentFilterValues,
  getCurrentAPIFilterValues,
  getAPIFilterValues,
  applyFilterValues,
} from '../widgets/FilterPanelNew';

import {
  getYearToDateRange,
  getCurrentQuarterRange,
  getCurrentYear,
  getToday,
} from '@/shared/date-utils';
import isEmpty from 'lodash-es/isEmpty';

export default {
  name: 'filters',

  components: {
    FilterPanelNew,
  },

  props: {
    apiRequestIsPerforming: {
      type: Boolean,
      default: () => true,
    },

    apiRequestData: {
      type: Object,
      default: () => ({
        voyages: {},
        voyagesPeriodicStat: {},
      }),
    },
  },

  data() {
    return {
      // common parameters
      autoRefresh: true,
      updatePending: false,
      filtersReady: false,
      defaultFilterValues: {}, // used to reset dashboard filters

      filterSchema: {
        // checkboxes
        excludeIntraCountry: {
          name: 'excludeIntraCountry',
          apiName: 'excludeIntraCountry',
          type: 'checkbox',
          label: 'Exclude intra country voyages',
          value: true,
          defaultChoice: true,
        },

        excludeUnknownDestinations: {
          name: 'excludeUnknownDestinations',
          apiName: 'excludeUnknownDestinations',
          type: 'checkbox',
          label: 'Exclude voyages with unknown destination',
          value: false,
          defaultChoice: false,
        },

        excludeParcel: {
          name: 'excludeParcel',
          apiName: 'parceling',
          type: 'checkbox',
          label: 'Exclude parcel voyages',
          value: false,
          defaultChoice: false,
        },

        dateRange: {
          name: ['startDate', 'endDate'],
          type: 'daterange-picker',
          options: {
            ranges: this.getDatePickerRanges(),
          },

          startDate: {
            name: 'startDate',
            apiName: 'fromDate',
            value: moment('2020-01-01').format('YYYY-MM-DD'),
          },
          endDate: {
            name: 'endDate',
            apiName: 'toDate',
            value: moment().format('YYYY-MM-DD'),
          },
        },

        flowDate: {
          name: 'flowDate',
          apiName: 'flowDate',
          type: 'select-buttons',
          label: 'Date to sort by',

          value: 'export', // override default choice
          choices: [
            // defaultChoices.metric,
            {text: 'Export Date', value: 'export'},
            {text: 'Import Date', value: 'import'},
          ],
          // defaultChoice: defaultChoices.metric.value,
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        subSegment: {
          name: 'subSegment',
          apiName: 'subSegment',
          type: 'treeselect',
          placeholder: 'Segment',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        originCountry: {
          name: 'originCountry',
          apiName: 'fromCountryCode',
          type: 'multiselect',
          placeholder: 'Origin country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        destinationCountry: {
          name: 'destinationCountry',
          apiName: 'toCountryCode',
          type: 'multiselect',
          placeholder: 'Destination country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        originRegion: {
          name: 'originRegion',
          apiName: 'fromRegion',
          type: 'multiselect-region',
          placeholder: 'Origin region',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        destinationRegion: {
          name: 'destinationRegion',
          apiName: 'toRegion',
          type: 'multiselect-region',
          placeholder: 'Destination region',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        vesselClass: {
          name: 'vesselClass',
          apiName: 'segment',
          type: 'multiselect',
          placeholder: 'Segment',
          value: [],
          choices: [
            {
              text: 'Capesize',
              value: 'capesize',
            },
            {
              text: 'Handysize',
              value: 'handysize',
            },
            {
              text: 'Panamax',
              value: 'panamax',
            },
            {
              text: 'Supramax',
              value: 'supramax',
            },
          ],
          defaultChoice: null,
        },
      }, // <-- end of filterSchema
    };
  },

  methods: {
    ...mapActions('api/tradeflows', [
      'getCommodities',
      'getSegments',
      'getCountries',
      'getRegions',
      'getTradeflowsRegionsWithPolygons',
    ]),

    async loadFilters() {
      await this.loadCommodityChoices();
      await this.loadSegmentChoices();
      await this.loadRegionsChoices();
      this.filtersReady = true;
    },

    async loadCommodityChoices() {
      this.filterSchema.commodity.choices = await this.getCommodities({
        group: true,
      });
    },

    async loadSegmentChoices() {
      this.filterSchema.subSegment.choices = await this.getSegments({
        group: true,
      });
    },

    async loadRegionsChoices() {
      const retrieveMethods = [
        async () => {
          // origin countries
          this.filterSchema.originCountry.choices = await this.getCountries();
        },
        async () => {
          // destination countries
          this.filterSchema.destinationCountry.choices = await this.getCountries();
        },
        async () => {
          // origin regions
          this.filterSchema.originRegion.choices = await this.getTradeflowsRegionsWithPolygons(
            {direction: 'export'},
          );
        },
        async () => {
          // destination regions
          this.filterSchema.destinationRegion.choices = await this.getTradeflowsRegionsWithPolygons(
            {direction: 'import'},
          );
        },
      ];

      await Promise.all(retrieveMethods.map(method => method()));
    },

    getDatePickerRanges() {
      let ranges = {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        QTD: getCurrentQuarterRange(),
        YTD: getYearToDateRange(),
      };

      const currentYear = getCurrentYear();
      for(let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`] = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate(),
        ];
      }

      return ranges;
    },

    setDefaultForSelect(fieldNames, resetChoices) {
      fieldNames.forEach(fieldName => {
        const field = this.filterSchema[fieldName];

        if(field.type === 'select') {
          field.value = field.defaultChoice.value;
        } else {
          field.value = field.defaultChoice ? [field.defaultChoice] : [];
        }

        if(resetChoices) {
          // in case of vessels we don't need to retrieve items, so it's not needed to reset select items
          field.choices = this.apiDataToChoices(fieldName, null);
        }
      });
    },

    apiDataToChoices(fieldName, data) {
      // adds default value for a select
      if(data) {
        if(this.filterSchema[fieldName].defaultChoice) {
          data.unshift(this.filterSchema[fieldName].defaultChoice);
        }
        return data;
      } else {
        return this.filterSchema[fieldName].defaultChoice || [];
      }
    },

    choicesToValues(choices) {
      return choices.map(item => item.value);
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);
      let apiFilterValues = getAPIFilterValues(this.filterSchema, filterValues);
      apiFilterValues = this.formatAPIFilterValues(apiFilterValues);

      if(this.autoRefresh) {
        this.$emit('search', apiFilterValues);
        this.updatePending = false;
      } else {
        this.updatePending = true;
      }
    },

    formatAPIFilterValues(apiFilterValues) {
      return {
        ...apiFilterValues,
        parceling: apiFilterValues.parceling ? 'exclude' : 'include',
      };
    },

    forceRefreshFilter() {
      let apiFilterValues = getCurrentAPIFilterValues(this.filterSchema);
      apiFilterValues = this.formatAPIFilterValues(apiFilterValues);
      this.$emit('search-immediately', apiFilterValues);
      this.updatePending = false;
    },

    resetSelectToDefaultIfRequired(field) {
      // if commidity is changed, new origin/destination countries list
      // can have choices without a currently selected value
      // this case it's required to reset select to default value and apply filters again
      // field is a "this.originCountry" or "this.destinationCountry" for example

      const choicesValues = field.choices.map(choice => choice.value);
      let existingValues = field.value.filter(item =>
        choicesValues.includes(item.value),
      );

      if(!existingValues.length) {
        if(field.defaultChoice) {
          field.value = field.defaultChoice.value;
        } else {
          field.value = [];
        }
      }
    },

    setDefaultValues() {
      if(!isEmpty(this.defaultFilterValues)) {
        applyFilterValues(
          this.filterSchema,
          cloneDeep(this.defaultFilterValues),
        );
      }

      // set default choice to "iron_ore" if available and apply filter (initial values)
      const selectedCommodity = this.filterSchema.commodity.choices.find(
        item => item.id === 'Iron Ore',
      );
      if(selectedCommodity) {
        this.filterSchema.commodity.value = selectedCommodity.children;
      }
    },
  },

  computed: {
    ...mapGetters('api/presets', ['getActivePreset']),

    filterGroups() {
      // we can't use common way with getFormattedFilters(), since 3 rows are required
      return [
        {
          title: null,
          items: [
            this.filterSchema.commodity,
            this.filterSchema.subSegment,
            this.filterSchema.dateRange,
            this.filterSchema.flowDate,
          ],
        },
        {
          title: null,
          items: [
            this.filterSchema.originRegion,
            this.filterSchema.originCountry,
            this.filterSchema.destinationRegion,
            this.filterSchema.destinationCountry,
          ],
        },
        {
          title: null,
          items: [
            this.filterSchema.excludeIntraCountry,
            this.filterSchema.excludeUnknownDestinations,
            this.filterSchema.excludeParcel,
          ],
        },
      ];
    },

    refreshButtonVariant() {
      if(!this.autoRefresh && this.updatePending) {
        return 'info';
      } else {
        return 'outline-secondary';
      }
    },
  },

  watch: {
    'filterSchema.originCountry.choices': function () {
      this.resetSelectToDefaultIfRequired(this.filterSchema.originCountry);
    },

    'filterSchema.destinationCountry.choices': function () {
      this.resetSelectToDefaultIfRequired(this.filterSchema.destinationCountry);
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilters();
          this.defaultFilterValues = cloneDeep(
            getCurrentFilterValues(this.filterSchema),
          );
          if(!preset) {
            this.setDefaultValues();
          }
        }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },

    autoRefresh(value) {
      if(value && this.updatePending) {
        this.forceRefreshFilter();
      }
    },
  },
};
</script>

<style scoped>
.filter-panel >>> .multiselect__tags {
  min-width: 200px;
}

.filter-panel >>> .custom-select {
  min-width: 150px;
}
</style>

<style scoped lang="scss">
.filter-container {
  padding: 10px;
}

.request-controls {
  display: flex;
  flex-direction: column;
  margin: 10px 0 5px 0;

  .checkbox {
    white-space: nowrap;
    margin-bottom: 5px;
  }

  .button {
    font-size: 0.85rem;
  }
}
</style>
